/**
 * Kursunabhängige Datenstrukturen.
 *
 * Ein Kurs besteht aus Modulen, ein Modul aus Lektionen und eine Lektion aus
 * Schritten. Jeder Schritt kann eine Aufgabe enthalten, die direkt im geöffneten
 * Office-Dokument geprüft wird. Die Prüfung selbst ist eine einfache Funktion –
 * dadurch bleibt die Engine unabhängig davon, ob gerade Word, Excel oder
 * PowerPoint geprüft wird.
 */

export type OfficeHost = "Word" | "Excel" | "PowerPoint";

export type CourseLevel = "Einsteiger" | "Fortgeschritten" | "Profi" | "Komplettkurs";

/**
 * Grobe Einordnung eines Moduls innerhalb eines Komplettkurses. Der Kurs bleibt
 * einer, die Einordnung hilft aber bei der Orientierung: Die Grundlagen reichen
 * für den Arbeitsalltag, der Aufbauteil geht darüber hinaus.
 */
export type ModuleLevel = "Grundlagen" | "Aufbau";

/** Einzelnes Teilkriterium einer Aufgabe, z. B. "Titel ist mindestens 16 pt groß". */
export interface CheckCriterion {
  label: string;
  passed: boolean;
}

/** Ergebnis einer Aufgabenprüfung. */
export interface CheckResult {
  passed: boolean;
  /** Kurze Rückmeldung an die lernende Person. */
  message: string;
  /** Zusätzlicher Tipp, wird nur bei nicht bestandener Prüfung angezeigt. */
  hint?: string;
  /** Optionale Einzelkriterien für mehrteilige Aufgaben. */
  criteria?: CheckCriterion[];
  /**
   * Die Aufgabe konnte in dieser Word-Version nicht geprüft werden, weil die
   * dafür nötige Schnittstelle fehlt. Die lernende Person kann den Schritt dann
   * selbst als erledigt markieren.
   */
  unsupported?: boolean;
}

/**
 * Prüffunktion einer Aufgabe. Wirft die Funktion einen Fehler, wird dieser von
 * der Engine abgefangen und als fehlgeschlagene Prüfung angezeigt.
 */
export type TaskCheck = () => Promise<CheckResult>;

/**
 * VBA-Prüfmakro zu einer Aufgabe.
 *
 * Für einen Teil der Office-Funktionen – Folienübergänge, Sprechernotizen,
 * Seriendruck, Sparklines, Szenarien und einige mehr – gibt es in Office.js
 * keine Schnittstelle. Das Prüfmakro liest die Angabe über das VBA-Objektmodell
 * aus und legt sie als benutzerdefinierten XML-Teil in der Datei ab, den die
 * Prüffunktion dann über Office.js liest. Einzelheiten stehen in
 * docs/vollstaendigkeit/MAKROS.md.
 */
export interface MacroSnippet {
  /** Kennung des Prüfberichts, den das Makro schreibt, z. B. "pp-uebergaenge". */
  pruefung: string;
  /** Anwendung, in der das Makro läuft. */
  host: OfficeHost;
  /** Name der Prozedur, wie er im Makro-Dialog (Alt + F8) erscheint. */
  name: string;
  /** Kurzer Titel für die Anzeige im Panel. */
  titel: string;
  /** Vollständiger VBA-Quelltext zum Einfügen in ein Modul. */
  code: string;
}

export interface LessonTask {
  /** Was soll im Dokument getan werden? */
  instruction: string;
  /** Optionaler Tipp, den die lernende Person aufklappen kann. */
  hint?: string;
  /** Prüfmakro, das vor der Prüfung einmal ausgeführt werden muss. */
  macro?: MacroSnippet;
  /**
   * Das Makro ist nur ein Ersatzweg: Neuere Office-Versionen liefern die
   * Angabe selbst, ältere (etwa Word 2019/2021) brauchen das Makro.
   */
  macroNurErsatz?: boolean;
  check: TaskCheck;
}

export interface LessonStep {
  id: string;
  title: string;
  /** Erklärender Fließtext, ein Eintrag pro Absatz. */
  explanation: string[];
  /** Optionale Merkliste, z. B. Tastenkürzel oder Faustregeln. */
  keyPoints?: string[];
  /** Ohne Aufgabe ist der Schritt ein reiner Wissensschritt. */
  task?: LessonTask;
}

export interface Lesson {
  id: string;
  title: string;
  summary: string;
  durationMinutes: number;
  learningGoals: string[];
  steps: LessonStep[];
}

export interface CourseModule {
  id: string;
  title: string;
  summary: string;
  level: ModuleLevel;
  lessons: Lesson[];
}

export interface Course {
  id: string;
  host: OfficeHost;
  title: string;
  summary: string;
  level: CourseLevel;
  modules: CourseModule[];
}

/** Eindeutiger Schlüssel eines Schritts innerhalb eines Kurses. */
export type StepKey = string;

export function stepKey(moduleId: string, lessonId: string, stepId: string): StepKey {
  return `${moduleId}/${lessonId}/${stepId}`;
}

export function lessonKey(moduleId: string, lessonId: string): string {
  return `${moduleId}/${lessonId}`;
}

/** Alle Lektionen eines Kurses in der Reihenfolge des Lehrplans. */
export function flattenLessons(course: Course): Array<{ module: CourseModule; lesson: Lesson }> {
  return course.modules.flatMap((module) => module.lessons.map((lesson) => ({ module, lesson })));
}

/** Anzahl aller Schritte eines Kurses (Basis für die Fortschrittsanzeige). */
export function countSteps(course: Course): number {
  return flattenLessons(course).reduce((total, { lesson }) => total + lesson.steps.length, 0);
}

/** Module eines Kurses, gruppiert nach ihrer Einordnung. */
export function modulesByLevel(course: Course): Array<{ level: ModuleLevel; modules: CourseModule[] }> {
  const levels: ModuleLevel[] = ["Grundlagen", "Aufbau"];
  return levels
    .map((level) => ({
      level,
      modules: course.modules.filter((module) => module.level === level),
    }))
    .filter((group) => group.modules.length > 0);
}
